
// Cookie consent utilities for GDPR/CCPA compliance
import { DateTime } from 'luxon';
export type CookieCategory = 'necessary' | 'analytics' | 'marketing' | 'preferences';

export interface CookiePreferences {
  necessary: boolean; // Always true, can't be disabled
  analytics: boolean;
  marketing: boolean;
  preferences: boolean;
  timestamp: number;
}

const COOKIE_CONSENT_KEY = 'scaleup_cookie_consent';
const COOKIE_CONSENT_VERSION = '1.0';

export const getDefaultPreferences = (): CookiePreferences => ({
  necessary: true,
  analytics: false,
  marketing: false,
  preferences: false,
  timestamp: DateTime.now().toUnixInteger(),
});

export const getCookiePreferences = (): CookiePreferences | null => {
  if (typeof window === 'undefined') return null;

  try {
    const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!stored) return null;

    const parsed = JSON.parse(stored);
    // Check if consent is still valid (within 1 year)
    const oneYear = 365 * 24 * 60 * 60;
    if (DateTime.now().toUnixInteger() - parsed.timestamp > oneYear) {
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
};

export const setCookiePreferences = (preferences: CookiePreferences): void => {
  if (typeof window === 'undefined') return;

  const toStore = {
    ...preferences,
    necessary: true, // Always true
    timestamp: DateTime.now().toUnixInteger(),
    version: COOKIE_CONSENT_VERSION,
  };

  localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(toStore));

  // Dispatch custom event for other parts of the app to listen to
  window.dispatchEvent(new CustomEvent('cookiePreferencesUpdated', { detail: toStore }));
};

export const hasConsented = (): boolean => {
  return getCookiePreferences() !== null;
};

export const resetCookiePreferences = (): void => {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(COOKIE_CONSENT_KEY);
};
